// services/template.service.js
import assistantClient, { handleApiError } from '../api/assistantClient';

/**
 * Servicio para gestionar Assistant Templates (Avatars)
 */
class TemplateService {
  /**
   * Crear un nuevo template
   * @param {Object} templateData - Datos del template
   * @param {string} templateData.name - Nombre del template (requerido)
   * @param {string} templateData.account_id - UUID de la cuenta (requerido)
   * @param {string} [templateData.description] - Descripción del template
   * @param {boolean} [templateData.is_default=false] - Si es template por defecto
   * @param {boolean} [templateData.is_public=false] - Si es template público
   * @param {string} [templateData.project_id] - UUID del proyecto (requerido si is_public=false)
   * @param {string} [templateData.avatar] - URL o base64 del avatar
   * @param {string[]} [templateData.tags] - Tags para categorización
   * @param {string} [templateData.language='es'] - Lenguaje del template (es, en, pt, fr)
   * @param {Object} [templateData.output_format] - Configuración de formato de salida
   * @param {string} [templateData.output_format.mode='normal'] - Modo (normal, json, markdown, html)
   * @param {boolean} [templateData.output_format.strict_validation=true] - Validación estricta
   * @param {boolean} [templateData.output_format.pretty_print=false] - Pretty print
   * @param {number} [templateData.output_format.indent=2] - Nivel de indentación
   * @param {Object} [templateData.output_format.provider_params={}] - Parámetros del proveedor
   * @param {Array<Object>} [templateData.sections] - Secciones del template
   * @returns {Promise<{success: boolean, data?: Object, error?: Object}>}
   */
  async createTemplate(templateData) {
    try {
      // Map frontend output_format.mode to backend enum values
      if (templateData.output_format && templateData.output_format.mode) {
        if (templateData.output_format.mode === 'json') {
          templateData.output_format.mode = 'structured_json';
        }
      }
      
      const response = await assistantClient.post('/assistant_templates', templateData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar templates públicos (is_public=true)
   * GET /assistant_templates/public
   * @param {Object} params - Query parameters
   * @param {number} [params.limit=100] - Maximum number of templates (1-1000)
   * @param {number} [params.offset=0] - Number of templates to skip
   * @param {string} [params.sort_by='updated_at'] - Field to sort by
   * @param {boolean} [params.descending=true] - Sort in descending order
   */
  async listPublicTemplates({ 
    limit = 100, 
    offset = 0,
    sort_by = 'updated_at',
    descending = true,
    view = 'full'
  } = {}) {
    try {
      const response = await assistantClient.get('/assistant_templates/public', {
        params: { limit, offset, sort_by, descending, view }
      });
      return { 
        success: true, 
        data: response.data || [],
        total: response.data?.length || 0,
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar templates accesibles para una cuenta específica
   * GET /assistant_templates/account/{account_id}
   * Retorna: templates públicos globales + templates privados de esta cuenta
   * @param {string} accountId - Account ID (required)
   * @param {Object} params - Query parameters
   * @param {number} [params.limit=100] - Maximum number of templates (1-1000)
   * @param {number} [params.offset=0] - Number of templates to skip
   * @param {string} [params.sort_by='updated_at'] - Field to sort by
   * @param {boolean} [params.descending=true] - Sort in descending order
   */
  async listAccountTemplates(accountId, { 
    limit = 100, 
    offset = 0,
    sort_by = 'updated_at',
    descending = true,
    view = 'full'
  } = {}) {
    try {
      if (!accountId) {
        return { success: false, error: 'account_id is required' };
      }
      
      const response = await assistantClient.get(`/assistant_templates/account/${accountId}`, {
        params: { limit, offset, sort_by, descending, view }
      });
      return { 
        success: true, 
        data: response.data || [],
        total: response.data?.length || 0,
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar templates con paginación (DEPRECATED - usar listPublicTemplates o listAccountTemplates)
   * @deprecated Use listPublicTemplates() for public templates or listAccountTemplates(accountId) for account templates
   */
  async listTemplates({ 
    limit = 100, 
    offset = 0, 
    defaults_only = false,
    view = 'full'
  } = {}) {
    try {
      const response = await assistantClient.get('/assistant_templates', {
        params: { limit, offset, defaults_only, view }
      });
      return { 
        success: true, 
        data: response.data || [],
        total: response.data?.length || 0,
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener template por ID
   */
  async getTemplate(templateId) {
    try {
      const response = await assistantClient.get(`/assistant_templates/${templateId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Actualizar template
   * @param {string} templateId - UUID del template a actualizar
   * @param {Object} updates - Campos a actualizar
   * @param {string} requesterAccountId - Account ID del requester (requerido para autorización)
   * @param {string} [updates.name] - Nombre del template
   * @param {string} [updates.description] - Descripción del template
   * @param {boolean} [updates.is_default] - Si es template por defecto
   * @param {boolean} [updates.is_public] - Si es template público
   * @param {string} [updates.project_id] - UUID del proyecto
   * @param {string} [updates.avatar] - URL o base64 del avatar
   * @param {string[]} [updates.tags] - Tags para categorización
   * @param {string} [updates.language] - Lenguaje del template (es, en, pt, fr)
   * @param {Object} [updates.output_format] - Configuración de formato de salida
   * @param {string} [updates.output_format.mode] - Modo (normal, json, markdown, html)
   * @param {Array<Object>} [updates.sections] - Secciones del template
   * @returns {Promise<{success: boolean, data?: Object, error?: Object}>}
   */
  async updateTemplate(templateId, updates, requesterAccountId) {
    try {
      if (!requesterAccountId) {
        return { success: false, error: 'requester_account_id is required' };
      }
      
      // Map frontend output_format.mode to backend enum values
      if (updates.output_format && updates.output_format.mode) {
        if (updates.output_format.mode === 'json') {
          updates.output_format.mode = 'structured_json';
        }
      }
      
      const response = await assistantClient.put(
        `/assistant_templates/${templateId}`,
        updates,
        { params: { requester_account_id: requesterAccountId } }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Eliminar template
   * DELETE /assistant_templates/{template_id}?requester_account_id={account_id}
   * @param {string} templateId - Template ID to delete
   * @param {string} requesterAccountId - Account ID of the requester (required for authorization)
   */
  async deleteTemplate(templateId, requesterAccountId) {
    try {
      if (!requesterAccountId) {
        return { success: false, error: 'requester_account_id is required' };
      }
      
      const response = await assistantClient.delete(`/assistant_templates/${templateId}`, {
        params: { requester_account_id: requesterAccountId }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar templates filtrados por tags
   * GET /assistant_templates/tags
   * Respects multi-tenant visibility: returns public templates plus account/project templates.
   * 
   * @param {Object} params - Query parameters
   * @param {string[]} params.tags - Tags to filter by (required). Can specify multiple tags.
   * @param {string} [params.account_id] - Filter by account. Returns public + account templates.
   * @param {string} [params.project_id] - Filter by project. Returns public + project templates.
   * @param {boolean} [params.include_public=true] - Include public templates in results.
   * @param {boolean} [params.match_all=false] - If true, match ALL tags (AND). If false, match ANY tag (OR).
   * @param {number} [params.limit=100] - Maximum number of templates (1-500)
   * @param {number} [params.offset=0] - Number of templates to skip
   * 
   * @example
   * // Public only
   * listTemplatesByTags({ tags: ['sales'] })
   * 
   * @example
   * // Public + Account
   * listTemplatesByTags({ tags: ['sales'], account_id: 'uuid' })
   * 
   * @example
   * // OR logic (sales OR marketing)
   * listTemplatesByTags({ tags: ['sales', 'marketing'] })
   * 
   * @example
   * // AND logic (sales AND fastshop)
   * listTemplatesByTags({ tags: ['sales', 'fastshop'], match_all: true })
   * 
   * @example
   * // Private only
   * listTemplatesByTags({ tags: ['sales'], account_id: 'uuid', include_public: false })
   */
  async listTemplatesByTags({ 
    tags = [],
    account_id = null,
    project_id = null,
    include_public = true,
    match_all = false,
    limit = 100,
    offset = 0,
    view = 'full'
  } = {}) {
    try {
      if (!tags || tags.length === 0) {
        return { success: false, error: 'At least one tag is required' };
      }

      // Build query params
      const params = new URLSearchParams();
      
      // Add multiple tags
      tags.forEach(tag => {
        params.append('tags', tag);
      });

      // Add optional params
      if (account_id) params.append('account_id', account_id);
      if (project_id) params.append('project_id', project_id);
      if (typeof include_public === 'boolean') params.append('include_public', include_public);
      if (match_all) params.append('match_all', match_all);
      if (limit) params.append('limit', limit);
      if (offset) params.append('offset', offset);
      if (view) params.append('view', view);

      const response = await assistantClient.get('/assistant_templates/tags', {
        params
      });
      
      return { 
        success: true, 
        data: response.data || [],
        total: response.data?.length || 0,
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }
}

// Exportar instancia única
const templateService = new TemplateService();
export default templateService;